import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Home, Crown, Send, ArrowRight } from 'lucide-react';
import axios from 'axios';

import { API_URL } from '../config';
const API = API_URL;

const WelcomePage = ({ user, onLogout }) => {
  const [feeds, setFeeds] = useState([]);
  const [telegramLink, setTelegramLink] = useState('');

  useEffect(() => {
    document.title = 'EbonyThroatQueens | Dashboard';
    fetchFeeds();
  }, []);

  const fetchFeeds = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/feeds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeeds(response.data.feeds || []);
      
      // Get telegram link from EliteEbony feed
      const eliteFeed = response.data.feeds.find(f => f.slug === 'eliteebony');
      if (eliteFeed?.telegram_link) {
        setTelegramLink(eliteFeed.telegram_link);
      }
    } catch (error) {
      console.error('Failed to fetch feeds:', error);
    }
  };

  const goToFeed = (slug) => {
    window.location.href = `/feed/${slug}`;
  };

  const openTelegram = () => {
    window.open('https://t.me/RedChannel_Hub', '_blank');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#050505' }}>
      <div className="max-w-md w-full">
        {/* Header with Channel-Style Logo */}
        <div className="text-center mb-12 mt-8">
          {/* Channel Icon Logo */}
          <div className="flex justify-center mb-4">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden" 
              style={{ 
                background: 'linear-gradient(135deg, #EE1C4E 0%, #FF6B9D 100%)',
                boxShadow: '0 4px 20px rgba(238, 28, 78, 0.4)'
              }}
            >
              <img 
                src="https://pub-cdd5f0ce51164f909259eb7b0c79f5fe.r2.dev/Logo/_b71d8978-0c1d-495b-b9c7-9ef797e3ab2b.jpeg" 
                alt="RedChannel Logo"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          
          <h1 className="text-5xl font-black mb-4" style={{ fontFamily: 'Chivo, sans-serif', color: '#EE1C4E' }}>
            REDCHANNEL
          </h1>
          <p className="text-lg text-white font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Welcome Back, {user?.name}
          </p>
          <p className="text-sm text-[#A1A1A1] mt-2 italic" style={{ fontFamily: 'Georgia, serif' }}>
            You've stepped behind velvet curtains. Discretion is expected.
          </p>
          <p className="text-sm text-[#525252] mt-3">
            Choose your destination
          </p>
        </div>

        {/* Feed Buttons */}
        <div className="space-y-4">
          {/* Home Button */}
          <button
            onClick={() => goToFeed('home')}
            className="w-full group"
            data-testid="home-feed-button"
          >
            <div className="bg-[#121212] border-2 border-[#2A2A2A] rounded-2xl p-6 transition-all hover:border-[#EE1C4E] hover:bg-[#1A1A1A]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#1A1A1A] flex items-center justify-center group-hover:bg-[#EE1C4E] transition-all">
                    <Home className="w-7 h-7 text-[#EE1C4E] group-hover:text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      HOME
                    </h3>
                    <p className="text-sm text-[#A1A1A1]">Main content feed</p>
                  </div>
                </div>
                <ArrowRight className="w-6 h-6 text-[#525252] group-hover:text-[#EE1C4E] transition-all" />
              </div>
            </div>
          </button>

          {/* EliteEbony PPV Button */}
          <button
            onClick={() => goToFeed('eliteebony')}
            className="w-full group"
            data-testid="eliteebony-feed-button"
          >
            <div className="bg-[#121212] border-2 border-[#2A2A2A] rounded-2xl p-6 transition-all hover:border-[#EE1C4E] hover:bg-[#1A1A1A]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#EE1C4E] to-[#FF6B9D] flex items-center justify-center">
                    <Crown className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      EliteEbony's PPV
                    </h3>
                    <p className="text-sm text-[#A1A1A1]">Exclusive premium content</p>
                  </div>
                </div>
                <ArrowRight className="w-6 h-6 text-[#525252] group-hover:text-[#EE1C4E] transition-all" />
              </div>
            </div>
          </button>

          {/* Telegram Button */}
          <button
            onClick={openTelegram}
            className="w-full group"
            data-testid="telegram-button"
          >
            <div className="bg-[#121212] border-2 border-[#2A2A2A] rounded-2xl p-6 transition-all hover:border-[#0088cc] hover:bg-[#1A1A1A]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#0088cc] flex items-center justify-center">
                    <Send className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      Join Telegram Channel
                    </h3>
                    <p className="text-sm text-[#A1A1A1]">Get instant updates</p>
                  </div>
                </div>
                <ArrowRight className="w-6 h-6 text-[#525252] group-hover:text-[#0088cc] transition-all" />
              </div>
            </div>
          </button>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <button
            onClick={onLogout}
            className="text-sm text-[#525252] hover:text-[#EE1C4E] transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;
